import type { CSSProperties, ReactElement } from 'react';
import {
  Children,
  cloneElement,
  forwardRef,
  useEffect,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import { useRefWrappedCallback } from '@elfsight/lib-utils';
import { defaultRect, invariant, useStableRect } from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import { computeColumns } from './computeColumns';
import { computeMasonry } from './computeMasonry';

export const MasonryContainer = styled.div`
  position: relative;
  transform: translate3d(0, 0, 0);
`;

export type MasonryItemDimension = {
  x: number;
  y: number;
  height: number;
  width: number;
};

export type MasonryProps = {
  children: ReactElement[];
  onEffectiveColumnsChange?: (effectiveColumns: number) => void;
  columns?: 'auto' | number;
  basis?: number;
  gap?: number | [number, number];
  disableStretch?: boolean;
  style?: CSSProperties;
  className?: string;
  updateHeights?: boolean;
};

export const Masonry = forwardRef<HTMLDivElement, MasonryProps>(
  function Masonry(
    {
      children,
      columns = 'auto',
      basis = 300,
      gap: derivedGap = 0,
      disableStretch = false,
      onEffectiveColumnsChange,
      style,
      updateHeights,
      ...forwardingProps
    },
    forwardingRef
  ) {
    const [itemsHeights, setItemsHeights] = useState<number[]>([]);

    const containerRef = useRef<HTMLDivElement | null>(null);

    const [containerRect, setContainerRect] = useState(defaultRect);
    const [containerRectRef] = useStableRect(containerRect, setContainerRect);

    const [computedColumns, setComputedColumns] = useState<
      [number, number, number] | undefined
    >(undefined);
    const [ready, setReady] = useState(false);
    const [height, setHeight] = useState(0);
    const [itemsDimensions, setItemsDimensions] = useState<
      MasonryItemDimension[]
    >([]);

    const containerMergedRef = useMergedRef(
      containerRectRef,
      containerRef,
      forwardingRef
    );

    invariant(
      columns === 'auto' || typeof columns === 'number',
      'Expected the columns to be "auto" or a number.'
    );

    invariant(
      typeof derivedGap === 'number' ||
        (Array.isArray(derivedGap) && derivedGap.length === 2),
      'Gap should have 1 value or exactly 2 - for vertical and horizontal.'
    );

    const gap = Array.isArray(derivedGap)
      ? derivedGap
      : (Array.from({ length: 2 }, () => derivedGap) as [number, number]);

    const [columnGap, rowGap] = gap;

    useEffect(() => {
      setComputedColumns(
        computeColumns({
          columns,
          gap: [columnGap, rowGap],
          basis,
          stretch: !disableStretch,
          containerWidth: containerRect.width
        })
      );
    }, [
      columns,
      columnGap,
      rowGap,
      basis,
      disableStretch,
      containerRect.width
    ]);

    useEffect(() => {
      setReady(!!computedColumns);
    }, [computedColumns]);

    useEffect(() => {
      const { current: containerElement } = containerRef;

      if (!containerElement) {
        return;
      }

      const innerItems =
        containerElement.querySelectorAll<HTMLElement>(':scope > *');

      const nextItemsHeights = Array.from(innerItems).map(
        ({ offsetHeight }) => offsetHeight
      );

      setItemsHeights(nextItemsHeights);
    }, [children, computedColumns, updateHeights]);

    useEffect(() => {
      if (!computedColumns) {
        return;
      }

      const [effectiveColumnsCount, effectiveColumnsWidth, effectiveIndent] =
        computedColumns;

      const [nextItemsDimensions, nextHeight] = computeMasonry({
        itemsHeights,
        columnsCount: effectiveColumnsCount,
        columnsWidth: effectiveColumnsWidth,
        gap: [columnGap, rowGap],
        indent: effectiveIndent
      });

      setItemsDimensions(nextItemsDimensions);
      setHeight(nextHeight);
    }, [itemsHeights, computedColumns, columnGap, rowGap]);

    const dispatchEffectiveColumnsChange = useRefWrappedCallback(
      (value: number) => {
        if (onEffectiveColumnsChange) {
          onEffectiveColumnsChange(value);
        }
      }
    );

    useEffect(() => {
      if (!computedColumns) {
        return;
      }

      const [effectiveColumnsCount] = computedColumns;

      dispatchEffectiveColumnsChange(effectiveColumnsCount);
    }, [computedColumns, dispatchEffectiveColumnsChange]);

    return (
      <MasonryContainer
        {...forwardingProps}
        ref={containerMergedRef}
        style={{ height, ...style }}
      >
        {ready &&
          Children.map(children, (child, index) => {
            const {
              x = 0,
              y = 0,
              width: itemWidth
            } = itemsDimensions[index] || {};

            const itemInlineStyle = {
              position: 'absolute',
              top: 0,
              left: 0,
              width: itemWidth,
              transform: `translate(${x}px, ${y}px)`
            } as CSSProperties;

            return cloneElement(child, {
              ...child.props,
              style: { ...itemInlineStyle, ...child.props.style }
            });
          })}
      </MasonryContainer>
    );
  }
);
